import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { AlertTriangle, TrendingDown, TrendingUp, RefreshCw } from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const Simulator = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [parameters, setParameters] = useState({
    revenue_change: 0,
    expense_change: 0,
    days_to_simulate: 60
  });

  const runSimulation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/simulate-crisis`,
        parameters,
        getAuthHeaders()
      );
      setSimulation(response.data);
    } catch (error) {
      toast.error("Errore nella simulazione");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runSimulation();
  }, []);

  const presetScenarios = [
    { name: "Scenario Base", revenue: 0, expense: 0 },
    { name: "Crisi Lieve", revenue: -15, expense: 10 },
    { name: "Crisi Media", revenue: -30, expense: 20 },
    { name: "Crisi Grave", revenue: -50, expense: 30 },
    { name: "Crescita", revenue: 20, expense: 5 }
  ];

  const applyScenario = (scenario) => {
    setParameters({
      ...parameters,
      revenue_change: scenario.revenue,
      expense_change: scenario.expense
    });
  };

  return (
    <div className="animate-fade-in" data-testid="simulator-page">
      <div className="page-header">
        <h1 className="page-title">Simulatore Crisi Cassa</h1>
        <p className="page-subtitle">Analizza scenari di stress finanziario</p>
      </div>

      {/* Alert if crisis detected */}
      {simulation?.is_critical && (
        <Card className="bg-[#FF3B30]/5 border-[#FF3B30]/20 rounded-2xl mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
              </div>
              <div>
                <p className="font-semibold text-[#FF3B30]">Attenzione: Crisi di Cassa Prevista</p>
                <p className="text-[#86868B]">
                  Con questi parametri, il saldo diventerà negativo al giorno {simulation.crisis_day + 1}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Controls */}
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Parametri Simulazione
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Revenue Change Slider */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-[#1D1D1F] flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#34C759]" />
                  Variazione Ricavi
                </Label>
                <span className={`font-semibold ${
                  parameters.revenue_change >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                }`}>
                  {parameters.revenue_change >= 0 ? '+' : ''}{parameters.revenue_change}%
                </span>
              </div>
              <Slider
                value={[parameters.revenue_change]}
                onValueChange={(value) => setParameters({ ...parameters, revenue_change: value[0] })}
                min={-80}
                max={50}
                step={5}
                className="[&_[role=slider]]:bg-[#007AFF] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5"
                data-testid="revenue-slider"
              />
              <div className="flex justify-between text-xs text-[#86868B]">
                <span>-80%</span>
                <span>0%</span>
                <span>+50%</span>
              </div>
            </div>

            {/* Expense Change Slider */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-[#1D1D1F] flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-[#FF3B30]" />
                  Variazione Spese
                </Label>
                <span className={`font-semibold ${
                  parameters.expense_change <= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                }`}>
                  {parameters.expense_change >= 0 ? '+' : ''}{parameters.expense_change}%
                </span>
              </div>
              <Slider
                value={[parameters.expense_change]}
                onValueChange={(value) => setParameters({ ...parameters, expense_change: value[0] })}
                min={-30}
                max={80}
                step={5}
                className="[&_[role=slider]]:bg-[#007AFF] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5"
                data-testid="expense-slider"
              />
              <div className="flex justify-between text-xs text-[#86868B]">
                <span>-30%</span>
                <span>0%</span>
                <span>+80%</span>
              </div>
            </div>

            {/* Days Slider */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-[#1D1D1F]">Giorni da Simulare</Label>
                <span className="font-semibold text-[#007AFF]">{parameters.days_to_simulate} gg</span>
              </div>
              <Slider
                value={[parameters.days_to_simulate]}
                onValueChange={(value) => setParameters({ ...parameters, days_to_simulate: value[0] })}
                min={30}
                max={90}
                step={15}
                className="[&_[role=slider]]:bg-[#007AFF] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5"
                data-testid="days-slider"
              />
            </div>

            <Button
              onClick={runSimulation}
              disabled={loading}
              className="w-full h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
              data-testid="run-simulation-btn"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Esegui Simulazione
            </Button>

            {/* Preset Scenarios */}
            <div className="pt-4 border-t border-black/5">
              <Label className="text-[#86868B] text-xs uppercase tracking-wider mb-3 block">
                Scenari Preimpostati
              </Label>
              <div className="space-y-2">
                {presetScenarios.map((scenario) => (
                  <Button
                    key={scenario.name}
                    variant="outline"
                    onClick={() => applyScenario(scenario)}
                    className="w-full justify-start h-10 rounded-xl border-[#D2D2D7] hover:bg-[#F5F5F7] text-[#1D1D1F]"
                    data-testid={`scenario-${scenario.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {scenario.name}
                    <span className="ml-auto text-xs text-[#86868B]">
                      {scenario.revenue >= 0 ? '+' : ''}{scenario.revenue}% / {scenario.expense >= 0 ? '+' : ''}{scenario.expense}%
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Chart */}
        <Card className="lg:col-span-2 bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Proiezione Saldo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]" data-testid="simulation-chart">
              {simulation ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={simulation.simulation}>
                    <defs>
                      <linearGradient id="colorSimBalance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={simulation.is_critical ? "#FF3B30" : "#007AFF"} stopOpacity={0.2}/>
                        <stop offset="95%" stopColor={simulation.is_critical ? "#FF3B30" : "#007AFF"} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis 
                      dataKey="day" 
                      tickFormatter={(value) => `G${value + 1}`}
                      stroke="#86868B"
                      fontSize={12}
                    />
                    <YAxis 
                      tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
                      stroke="#86868B"
                      fontSize={12}
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Saldo']}
                      labelFormatter={(label) => `Giorno ${label + 1}`}
                      contentStyle={{ 
                        background: 'white', 
                        border: 'none', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                      }}
                    />
                    <ReferenceLine y={0} stroke="#FF3B30" strokeDasharray="5 5" />
                    {simulation.crisis_day && (
                      <ReferenceLine 
                        x={simulation.crisis_day} 
                        stroke="#FF3B30" 
                        label={{ value: 'CRISI', fill: '#FF3B30', fontSize: 12 }} 
                      />
                    )}
                    <Area 
                      type="monotone" 
                      dataKey="balance" 
                      stroke={simulation.is_critical ? "#FF3B30" : "#007AFF"} 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorSimBalance)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="spinner" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      {simulation && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <p className="stat-card-label">Saldo Iniziale</p>
              <p className="stat-card-value">{formatCurrency(simulation.initial_balance)}</p>
            </CardContent>
          </Card>
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <p className="stat-card-label">Saldo Finale</p>
              <p className={`stat-card-value ${simulation.final_balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                {formatCurrency(simulation.final_balance)}
              </p>
            </CardContent>
          </Card>
          <Card className="stat-card card-hover">
            <CardContent className="pt-6">
              <p className="stat-card-label">Variazione</p>
              <p className={`stat-card-value ${
                (simulation.final_balance - simulation.initial_balance) >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
              }`}>
                {formatCurrency(simulation.final_balance - simulation.initial_balance)}
              </p>
            </CardContent>
          </Card>
          <Card className={`stat-card card-hover ${simulation.is_critical ? 'border-[#FF3B30]/20 bg-[#FF3B30]/5' : ''}`}>
            <CardContent className="pt-6">
              <p className="stat-card-label">Stato</p>
              <p className={`stat-card-value text-xl ${simulation.is_critical ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                {simulation.is_critical ? (
                  <>
                    <AlertTriangle className="inline w-6 h-6 mr-2" />
                    Critico
                  </>
                ) : (
                  'Stabile'
                )}
              </p>
              {simulation.crisis_day && (
                <p className="text-sm text-[#86868B] mt-2">
                  Crisi al giorno {simulation.crisis_day + 1}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Simulator;
